/**
 * The address labels and the cover sheet, as PDF bytes.
 *
 * Kelsey gets three files for every neighbour mailing (ADR 0149): the letter,
 * which Google exports from the master document; a sheet of address labels for
 * the envelopes; and a cover sheet telling her what she is holding and what to
 * do with it. The letter comes out of Drive. The other two are made here.
 *
 * ## Why a PDF is written by hand
 *
 * Every other PDF in Roof Board is drawn by jsPDF in the browser, at the moment
 * somebody presses a button. This one cannot be: the packet is built by the
 * queue worker, in an Edge Function, with nobody in the room — that is the
 * whole point of the queue. Deno has no jsPDF that is worth the dependency, and
 * a label sheet is text at fixed coordinates, which is the one thing the PDF
 * format is genuinely simple about: a page, a base-14 font that every reader
 * already has, and one `Tj` per line.
 *
 * So it is written out longhand, and it lives in `packages/core` where CI runs
 * it, rather than in the function where nothing type-checks. The Edge Function
 * carries a transcription, pinned by `supabase/test/neighbour-letter-match.test.ts`.
 *
 * ## The two things that go wrong in a hand-written PDF
 *
 *   1. **The cross-reference table is byte offsets.** Every object's position
 *      in the file is written into `xref`, and a reader that finds an object
 *      one byte from where it was promised shows a blank page rather than an
 *      error. So the file is assembled as bytes and the offsets are counted as
 *      bytes — not as characters, which are not the same thing the moment an
 *      address carries an accent.
 *   2. **Text is not UTF-8.** A base-14 font is single-byte, WinAnsi, so
 *      anything outside it has to be folded down before it is written rather
 *      than left to arrive as two bytes of mojibake on an envelope.
 */

/** A sheet of labels: what it is called, and where every label sits on it. */
export interface LabelStock {
  /** Stored in `company_settings.neighbour_label_stock`. */
  key: string;
  /** What it is called on the Tools screen and on the cover sheet. */
  name: string;
  columns: number;
  rows: number;
  /** Points, 72 to the inch, which is the unit a PDF works in. */
  pageWidth: number;
  pageHeight: number;
  /** The gap from the page edge to the first label. */
  marginLeft: number;
  marginTop: number;
  labelWidth: number;
  labelHeight: number;
  columnGap: number;
  rowGap: number;
  /** Where the text starts inside its label. */
  paddingLeft: number;
  paddingTop: number;
  fontSize: number;
}

const INCH = 72;

/**
 * The stocks the office can buy, and the one it buys.
 *
 * Configurable because Josh asked for it to be — the label a printer has in
 * the cupboard is not a fact about this feature — and two entries rather than
 * a free-form geometry form, because "3 across by 10 down, 0.1875 inch left
 * margin" is a thing to get wrong and "Avery 5160" is a thing to read off a
 * box.
 */
export const LABEL_STOCKS: Record<string, LabelStock> = {
  avery_5160: {
    key: "avery_5160",
    name: "Avery 5160 — 30 per sheet (1 × 2-5/8 in)",
    columns: 3,
    rows: 10,
    pageWidth: 8.5 * INCH,
    pageHeight: 11 * INCH,
    marginLeft: 0.1875 * INCH,
    marginTop: 0.5 * INCH,
    labelWidth: 2.625 * INCH,
    labelHeight: 1 * INCH,
    columnGap: 0.125 * INCH,
    rowGap: 0,
    paddingLeft: 0.18 * INCH,
    paddingTop: 0.19 * INCH,
    fontSize: 10,
  },
  avery_5163: {
    key: "avery_5163",
    name: "Avery 5163 — 10 per sheet (2 × 4 in)",
    columns: 2,
    rows: 5,
    pageWidth: 8.5 * INCH,
    pageHeight: 11 * INCH,
    marginLeft: 0.15625 * INCH,
    marginTop: 0.5 * INCH,
    labelWidth: 4 * INCH,
    labelHeight: 2 * INCH,
    columnGap: 0.1875 * INCH,
    rowGap: 0,
    paddingLeft: 0.25 * INCH,
    paddingTop: 0.4 * INCH,
    fontSize: 12,
  },
};

/** What the office buys today. */
export const DEFAULT_LABEL_STOCK = "avery_5160";

/**
 * The stock a key names, or the default.
 *
 * Never throws and never returns nothing: a settings row naming a stock that
 * has since been taken out of the list must still print labels, on the stock
 * everybody has, rather than blocking a mailing over a dropdown.
 */
export function labelStock(key: string | null | undefined): LabelStock {
  const wanted = String(key ?? "").trim();
  return LABEL_STOCKS[wanted] ?? LABEL_STOCKS[DEFAULT_LABEL_STOCK]!;
}

/** How many labels one sheet of a stock holds. */
export const labelsPerSheet = (stock: LabelStock): number =>
  stock.columns * stock.rows;

/** One envelope's worth of address. */
export interface LabelAddress {
  /** Almost always absent: these go to a household nobody has met. */
  name?: string | null;
  line1: string;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
}

/**
 * The lines actually printed on one label.
 *
 * "Current Resident" rather than a blank first line, and rather than a name:
 * these are twenty households whose names Roof Board does not know and has no
 * business guessing. It is also what makes the envelope deliverable when the
 * house has changed hands, which on a street being reroofed is not rare.
 */
export const OCCUPANT_LINE = "Current Resident";

export function labelLines(address: LabelAddress): string[] {
  const lines: string[] = [];
  const name = String(address.name ?? "").trim();
  lines.push(name === "" ? OCCUPANT_LINE : name);
  lines.push(String(address.line1 ?? "").trim());
  const line2 = String(address.line2 ?? "").trim();
  if (line2 !== "") lines.push(line2);
  const city = String(address.city ?? "").trim();
  const state = String(address.state ?? "").trim();
  const zip = String(address.postalCode ?? "").trim();
  const last = [city, [state, zip].filter(Boolean).join(" ")]
    .filter((part) => part !== "")
    .join(", ");
  if (last !== "") lines.push(last);
  return lines.filter((line) => line !== "");
}

// ---------------------------------------------------------------------------
// The PDF itself
// ---------------------------------------------------------------------------

/** One run of text, placed from the bottom-left of the page as PDF measures. */
interface TextRun {
  x: number;
  y: number;
  size: number;
  text: string;
  bold?: boolean;
}

interface Page {
  width: number;
  height: number;
  runs: TextRun[];
}

/**
 * Typographic characters this system writes, as something a base-14 font has.
 *
 * Every one of these was in the first cover sheet printed: the em dash between
 * a letter's name and its street, the arrow in "Marketing → Neighbour
 * letters", the ⅝ in the label stock's own name. Left to the fallback below
 * they all became spaces, and the sheet read "Avery 5160  30 per sheet (1" ×
 * 2 ")" — which is not the box in the cupboard.
 *
 * Only what WinAnsi genuinely lacks. The multiplication sign is in it and
 * prints correctly, so swapping it for an "x" would make the sheet worse.
 */
const TRANSLITERATE: Record<string, string> = {
  "\u2014": "-",
  "\u2013": "-",
  "\u2192": ">",
  "\u2018": "'",
  "\u2019": "'",
  "\u201c": '"',
  "\u201d": '"',
  "\u2026": "...",
  "\u2022": "-",
  "\u00bd": "1/2",
  "\u00bc": "1/4",
  "\u00be": "3/4",
  "\u215b": "1/8",
  "\u215c": "3/8",
  "\u215d": "5/8",
  "\u215e": "7/8",
  "\u2032": "'",
  "\u2033": '"',
  "\u00a0": " ",
};

/**
 * A string as a PDF literal: WinAnsi bytes, with the three characters that
 * would otherwise end it escaped.
 *
 * Accents are folded rather than dropped — "Peña" becomes "Pena", which is
 * wrong on an envelope and arrives, where two bytes of mojibake is wrong and
 * looks like a broken system. Anything still outside the single-byte range
 * after both the folding and the table above becomes a space rather than a
 * black box.
 */
function pdfText(value: string): string {
  const folded = value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .normalize("NFC");
  let out = "";
  for (const character of folded) {
    const swapped = TRANSLITERATE[character] ?? character;
    for (const glyph of swapped) {
      const code = glyph.codePointAt(0) ?? 32;
      if (glyph === "(" || glyph === ")" || glyph === "\\") {
        out += `\\${glyph}`;
      } else if (code < 32) {
        out += " ";
      } else if (code <= 255) {
        out += glyph;
      } else {
        out += " ";
      }
    }
  }
  return out;
}

/** Roughly how wide a run of Helvetica is, for truncating an over-long line. */
const APPROXIMATE_WIDTH = 0.52;

/** How many characters of this size fit across a width. */
const charactersAcross = (size: number, width: number): number =>
  Math.max(1, Math.floor(width / (size * APPROXIMATE_WIDTH)));

/**
 * A line cut to the width it has, with an ellipsis where it was cut.
 *
 * For a label, where there is one line's worth of room and a longer address
 * has to lose something. Prose uses `wrapText` instead — the first cover sheet
 * printed ended "They are on the labels as", and a sentence that stops is
 * worse than no sentence.
 */
export function fitText(text: string, size: number, width: number): string {
  const limit = charactersAcross(size, width);
  const trimmed = text.trim();
  if (trimmed.length <= limit) return trimmed;
  return `${trimmed.slice(0, Math.max(1, limit - 1)).trimEnd()}…`;
}

/** A sentence broken across as many lines as it needs. */
export function wrapText(text: string, size: number, width: number): string[] {
  const limit = charactersAcross(size, width);
  const lines: string[] = [];
  let line = "";
  for (const word of text.trim().split(/\s+/)) {
    if (word === "") continue;
    const candidate = line === "" ? word : `${line} ${word}`;
    if (candidate.length <= limit) {
      line = candidate;
      continue;
    }
    if (line !== "") lines.push(line);
    // A single word longer than the line — a URL, a very long street — is cut
    // rather than allowed to run off the page.
    line = word.length <= limit ? word : fitText(word, size, width);
  }
  if (line !== "") lines.push(line);
  return lines.length === 0 ? [""] : lines;
}

/** WinAnsi: one byte per character, which is what a base-14 font reads. */
function latin1(value: string): Uint8Array {
  const bytes = new Uint8Array(value.length);
  for (let i = 0; i < value.length; i += 1) {
    bytes[i] = value.charCodeAt(i) & 0xff;
  }
  return bytes;
}

function contentStream(page: Page): string {
  const parts: string[] = [];
  for (const run of page.runs) {
    const font = run.bold ? "/F2" : "/F1";
    parts.push(
      `BT ${font} ${run.size} Tf 1 0 0 1 ${run.x.toFixed(2)} ` +
        `${run.y.toFixed(2)} Tm (${pdfText(run.text)}) Tj ET`,
    );
  }
  return parts.join("\n");
}

/**
 * Pages of placed text, as one PDF file.
 *
 * Objects are written in order and their byte offsets collected as they go,
 * because that is the only way to get `xref` right; see the header.
 */
function buildPdf(pages: Page[]): Uint8Array {
  const chunks: Uint8Array[] = [];
  let length = 0;
  const offsets: number[] = [];

  const push = (value: string, binary?: Uint8Array) => {
    const bytes = binary ?? latin1(value);
    chunks.push(bytes);
    length += bytes.length;
  };

  const object = (body: string) => {
    offsets.push(length);
    push(`${offsets.length} 0 obj\n${body}\nendobj\n`);
  };

  push("%PDF-1.4\n");
  // A comment of high bytes, which is how a PDF says "this file is binary" to
  // anything that might otherwise line-end-convert it in transit.
  push("%", new Uint8Array([0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a]));

  // 1: the catalogue. 2: the page tree. 3 and 4: the two fonts. Then a
  // content stream and a page object for each page, in that order, so the
  // numbering below can be worked out rather than tracked.
  const firstPageObject = 5;
  const pageObjectNumber = (index: number) => firstPageObject + index * 2 + 1;

  object("<< /Type /Catalog /Pages 2 0 R >>");
  object(
    `<< /Type /Pages /Count ${pages.length} /Kids [` +
      pages.map((_, i) => `${pageObjectNumber(i)} 0 R`).join(" ") +
      "] >>",
  );
  object(
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica " +
      "/Encoding /WinAnsiEncoding >>",
  );
  object(
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold " +
      "/Encoding /WinAnsiEncoding >>",
  );

  pages.forEach((page) => {
    const stream = contentStream(page);
    object(
      `<< /Length ${latin1(stream).length} >>\nstream\n${stream}\nendstream`,
    );
    object(
      "<< /Type /Page /Parent 2 0 R /MediaBox " +
        `[0 0 ${page.width.toFixed(2)} ${page.height.toFixed(2)}] ` +
        "/Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> " +
        `/Contents ${offsets.length} 0 R >>`,
    );
  });

  const xrefAt = length;
  const rows = ["xref", `0 ${offsets.length + 1}`, "0000000000 65535 f "];
  for (const offset of offsets) {
    rows.push(`${String(offset).padStart(10, "0")} 00000 n `);
  }
  push(`${rows.join("\n")}\n`);
  push(
    `trailer\n<< /Size ${offsets.length + 1} /Root 1 0 R >>\n` +
      `startxref\n${xrefAt}\n%%EOF\n`,
  );

  const file = new Uint8Array(length);
  let at = 0;
  for (const chunk of chunks) {
    file.set(chunk, at);
    at += chunk.length;
  }
  return file;
}

/**
 * A sheet — or several — of address labels.
 *
 * Filled across and then down, which is the order a label sheet is read and
 * the order a part-used sheet is reloaded in. A short last sheet is left short
 * rather than padded: a blank label is a label, and the count on the cover
 * sheet is what says whether anything is missing.
 */
export function labelSheetPdf(
  addresses: LabelAddress[],
  stock: LabelStock = labelStock(DEFAULT_LABEL_STOCK),
): Uint8Array {
  const perSheet = labelsPerSheet(stock);
  const sheets = Math.max(1, Math.ceil(addresses.length / perSheet));
  const pages: Page[] = [];

  for (let sheet = 0; sheet < sheets; sheet += 1) {
    const runs: TextRun[] = [];
    const slice = addresses.slice(sheet * perSheet, (sheet + 1) * perSheet);

    slice.forEach((address, index) => {
      const column = index % stock.columns;
      const row = Math.floor(index / stock.columns);
      const left =
        stock.marginLeft + column * (stock.labelWidth + stock.columnGap);
      // PDF measures up from the bottom of the page; labels are counted down
      // from the top, which is the one conversion in this file.
      const top =
        stock.pageHeight -
        stock.marginTop -
        row * (stock.labelHeight + stock.rowGap);

      const lines = labelLines(address);
      const usable = stock.labelWidth - stock.paddingLeft * 2;
      const leading = stock.fontSize + 2;
      lines.forEach((line, lineIndex) => {
        runs.push({
          x: left + stock.paddingLeft,
          y: top - stock.paddingTop - stock.fontSize - lineIndex * leading,
          size: stock.fontSize,
          text: fitText(line, stock.fontSize, usable),
        });
      });
    });

    pages.push({ width: stock.pageWidth, height: stock.pageHeight, runs });
  }

  return buildPdf(pages);
}

/** What the cover sheet says. Assembled by `coverSheet` in neighbourLetters. */
export interface CoverSheet {
  title: string;
  lines: string[];
  /** Printed under a rule at the foot, in the order given. */
  steps: string[];
}

/** The instruction sheet that rides on top of the packet. */
export function coverSheetPdf(cover: CoverSheet): Uint8Array {
  const width = 8.5 * INCH;
  const height = 11 * INCH;
  const left = INCH;
  const usable = width - INCH * 2;
  const runs: TextRun[] = [];
  let y = height - INCH;

  runs.push({ x: left, y, size: 18, text: cover.title, bold: true });
  y -= 30;

  for (const line of cover.lines) {
    if (line === "") {
      y -= 8;
      continue;
    }
    for (const wrapped of wrapText(line, 12, usable)) {
      runs.push({ x: left, y, size: 12, text: wrapped });
      y -= 18;
    }
  }

  y -= 14;
  runs.push({ x: left, y, size: 13, text: "What to do", bold: true });
  y -= 22;
  cover.steps.forEach((step, index) => {
    for (const wrapped of wrapText(`${index + 1}. ${step}`, 12, usable)) {
      runs.push({ x: left, y, size: 12, text: wrapped });
      y -= 18;
    }
  });

  return buildPdf([{ width, height, runs }]);
}

/** Bytes as base64, for a MIME attachment or a storage upload. */
export function base64Bytes(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  // `btoa` is in Node 16+, Deno and every browser; `Buffer` is in none of them.
  return btoa(binary);
}
